import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { Breadcrumbs } from './Breadcrumbs'
import { MobileNav } from './MobileNav'
import { CommandPaletteDialog } from '@/components/shared/CommandPaletteDialog'
import { KeyboardShortcutsModal } from '@/components/shared/KeyboardShortcutsModal'
import { MarketingNudge } from '@/components/onboarding/MarketingNudge'
import { useUiStore } from '@/stores/uiStore'
import { useWebSocket } from '@/hooks/useWebSocket'
import { useWebSocketInvalidation } from '@/hooks/useWebSocketInvalidation'
import { useScopeReset } from '@/hooks/useScopeReset'
import { useAuthStore } from '@/stores/authStore'

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { sidebarCollapsed, toggleSidebar, mobileSidebarOpen, setMobileSidebarOpen } = useUiStore()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  // Session-expiry listener — apiFetch dispatches `qyngent:session-expired`
  // when an authenticated request comes back 401, after clearing the
  // dead token from localStorage. We also need to:
  //   1. Clear `useAuthStore.user` — otherwise LoginPage sees the user
  //      as still signed in and bounces them straight back to the
  //      dashboard (and the next request 401s again, forever).
  //      `signOut()` from lib/auth handles both: removes the token
  //      (idempotent — already done by apiFetch) AND emits SIGNED_OUT
  //      so the AuthProvider purges the zustand store + downstream
  //      session caches via clearAllSessionState().
  //   2. Route to /login with a banner so the bounce doesn't look
  //      like a mystery logout.
  //
  // Idempotent: dropping a duplicate event when we're already on
  // /login keeps a parallel 401 storm from causing navigation churn.
  useEffect(() => {
    const onSessionExpired = () => {
      if (location.pathname === '/login') return
      // 1. Clear zustand auth state SYNCHRONOUSLY so the LoginPage sees
      //    `user === null` on its very next render. Without this, the
      //    LoginPage's `if (user) return <Navigate to="/dashboard" />`
      //    bounces the user straight back to the dashboard, where the
      //    next batch of queries 401s again — an infinite redirect.
      useAuthStore.getState().logout()
      // 2. Fire signOut() lazy + fire-and-forget so SIGNED_OUT reaches
      //    the AuthProvider listener → clearAllSessionState() purges
      //    the downstream stores (userStore, etc.) that hold cached
      //    profile/wallet/org data. Mirrors the manual sign-out
      //    pattern from Sidebar.tsx / TopBar.tsx.
      import('@/lib/auth').then((m) => m.signOut()).catch(() => {
        /* the localStorage token is already gone; downstream state
           will catch up on the next page render */
      })
      // 3. Route to /login with a banner so the bounce reads as
      //    "session expired" rather than mystery logout.
      const returnTo = encodeURIComponent(location.pathname + location.search)
      navigate(`/login?expired=1&returnTo=${returnTo}`, { replace: true })
    }
    window.addEventListener('qyngent:session-expired', onSessionExpired)
    return () => window.removeEventListener('qyngent:session-expired', onSessionExpired)
  }, [location.pathname, location.search, navigate])

  // Stale-scope self-heal for BOTH tenant headers (X-Workspace-Id and
  // X-Org-Id). See useScopeReset for why the backend answers with a fallback
  // + reset header instead of a 403.
  useScopeReset()

  // Global WebSocket connection — opens the socket + publishes it on
  // wsStore.socket so any subscriber can attach.
  useWebSocket()
  // Routes incoming WS frames to `queryClient.invalidateQueries()` for
  // the affected query keys, replacing the previous timer-based polling
  // on the dashboard (useVerification 5s, useApprovals 5s/15s, etc.).
  // See dashboard-ui/src/hooks/useWebSocketInvalidation.ts for the
  // channel → query-key mapping.
  useWebSocketInvalidation()

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024
      setIsMobile(mobile)
      if (!mobile) setMobileSidebarOpen(false)
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [setMobileSidebarOpen])

  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [location, setMobileSidebarOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '?') return
      const active = document.activeElement
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active?.hasAttribute('contenteditable')
      ) {
        return
      }
      setShortcutsOpen(true)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const sidebarWidth = sidebarCollapsed ? 80 : 280

  // Match the build ROUTE, not one exact path.
  //
  // This was `=== '/build'`, so the moment /build resolved a project and
  // rewrote the URL to /build/:projectId/:applicationId it stopped counting as
  // the build page and fell through to the generic document shell: content
  // capped at max-w-7xl and centred, px-6 gutters, min-h-screen instead of
  // h-screen, and MarketingNudge + Breadcrumbs injected above it. The workspace
  // rendered 269px narrower and shifted 135px right for the same user on the
  // same screen (measured in prod 2026-09-16: panel 1249px -> 980px), which is
  // the "centered vs expanded" the user reported.
  //
  // `startsWith('/build/')` with the trailing slash so a future `/buildings`
  // route cannot be swallowed by it.
  const isBuildPage =
    location.pathname === '/build' || location.pathname.startsWith('/build/')

  return (
    <div className={isBuildPage ? "h-screen bg-(--bg-primary) text-white flex flex-col overflow-hidden" : "min-h-screen bg-(--bg-primary) text-white flex flex-col"}>
      <Sidebar
        isCollapsed={sidebarCollapsed}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        isMobileOpen={mobileSidebarOpen}
        closeMobileSidebar={() => setMobileSidebarOpen(false)}
      />
      <TopBar
        isCollapsed={sidebarCollapsed}
        isMobile={isMobile}
        toggleMobileSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
      />
      <main
        className={
          isBuildPage
            ? 'flex-1 pt-16 transition-all duration-300 flex flex-col overflow-hidden'
            : 'flex-1 pt-16 px-6 transition-all duration-300 flex flex-col'
        }
        style={{ marginLeft: isMobile ? 0 : sidebarWidth }}
      >
        {isBuildPage ? (
          <div className="w-full flex-1 flex flex-col overflow-hidden">
            {children}
          </div>
        ) : (
          // 1536px rather than max-w-7xl's 1280.
          //
          // The old cap left 269px of the content area unused at a 1844px
          // viewport, on every page in the app, and ~50 of the 104 pages carry
          // tables, 4-6 column grids or horizontally scrolling regions that
          // were being squeezed by it. Tailwind v4 dropped max-w-screen-*, so
          // this is an explicit arbitrary value.
          //
          // Pages that want a reading measure are unaffected: 26 of them
          // already set their own narrower max-w-* on the content inside, and
          // that still wins. This only raises the ceiling.
          <div className="max-w-[1536px] mx-auto w-full flex-1 flex flex-col pb-6">
            <MarketingNudge />
            <Breadcrumbs />
            {children}
          </div>
        )}
      </main>
      {!isBuildPage && <MobileNav />}
      <CommandPaletteDialog />
      <KeyboardShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  )
}
